import { isNativeApp } from '../native';
import { ApiClientError } from '../api/client';
import { pushApi, type PushDevice } from '../api/endpoints';

/**
 * Mitteilungen auf dem Gerät per Web-Push (Plan 17, W3-Push-a): für den Browser und die installierte Web-App (PWA). Die
 * Erlaubnis des Geräts wird NUR nach einem Tipp auf „Mitteilungen einschalten“ erfragt, nie beim Start. Das Abo (Adresse und
 * Schlüssel des Browsers) geht nur an den Server der Person; die Oberfläche merkt sich lokal nur die Kennung des Abos, damit
 * „dieses Gerät“ erkennbar ist und das Abo beim Abmelden mitgelöscht wird.
 */

/** Fehler mit einem Text, den die Oberfläche der Person direkt zeigen darf (Erlaubnis, Gerät, Browser). */
export class PushError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PushError';
  }
}

/** Warum Mitteilungen auf diesem Gerät nicht gehen. */
export type PushBlock =
  /** Browser ohne Unterstützung (Service Worker, Push, Mitteilungen). */
  | 'browser'
  /** iPhone/iPad: erst nach „Zum Home-Bildschirm“ (iOS ab 16.4, nur installierte Web-App). */
  | 'ios-install'
  /** Native App: Mitteilungen in der App folgen später, bis dahin gilt der E-Mail-Hinweis. */
  | 'native'
  /** Das Gerät hat Mitteilungen für BARSO blockiert (nur in den Einstellungen des Geräts änderbar). */
  | 'denied';

export type PushSupport = { ok: true } | { ok: false; reason: PushBlock };

const STORAGE_KEY = 'barso.push.subscriptionId';

function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (/iPhone|iPad|iPod/.test(navigator.userAgent)) return true;
  // iPadOS meldet sich als Mac, hat aber einen Touchscreen
  return /Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1;
}

function isInstalledApp(): boolean {
  try {
    if ((navigator as Navigator & { standalone?: boolean }).standalone === true) return true;
    return window.matchMedia('(display-mode: standalone)').matches;
  } catch {
    return false;
  }
}

/** Kann dieses Gerät Mitteilungen bekommen, und wenn nicht: warum nicht? */
export function pushSupport(): PushSupport {
  if (isNativeApp()) return { ok: false, reason: 'native' };
  const capable =
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window;
  if (!capable) {
    // Safari auf dem iPhone kennt PushManager erst als installierte Web-App
    return { ok: false, reason: isIos() && !isInstalledApp() ? 'ios-install' : 'browser' };
  }
  if (Notification.permission === 'denied') return { ok: false, reason: 'denied' };
  return { ok: true };
}

/** Kennung des Abos dieses Geräts (nur lokal gemerkt), oder `null`. */
export function rememberedSubscriptionId(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function remember(id: string | null): void {
  try {
    if (id === null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // Ohne Speicher (privates Fenster): „dieses Gerät“ ist dann nur in der Liste nicht markiert
  }
}

/** Base64url-Schlüssel (VAPID) als Bytes für `pushManager.subscribe`. */
export function keyToBytes(key: string): Uint8Array<ArrayBuffer> {
  const padded = key
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(key.length / 4) * 4, '=');
  const raw = atob(padded);
  const bytes = new Uint8Array(new ArrayBuffer(raw.length));
  for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index);
  return bytes;
}

const READY_TIMEOUT_MS = 6000;

/** Der aktive Service Worker. Ohne Registrierung (Entwicklung, gerade erst installiert) kommt nie eine Antwort: Zeitlimit. */
async function registration(): Promise<ServiceWorkerRegistration> {
  const timeout = new Promise<never>((_resolve, reject) => {
    setTimeout(() => {
      reject(
        new PushError(
          'Der Dienst für Mitteilungen ist noch nicht bereit. Bitte laden Sie die Seite neu.',
        ),
      );
    }, READY_TIMEOUT_MS);
  });
  return Promise.race([navigator.serviceWorker.ready, timeout]);
}

/** Wartet eine neue Version der App auf das Schließen (der alte Service Worker kennt noch keine Mitteilungen)? */
export async function updateWaiting(): Promise<boolean> {
  try {
    const found = await navigator.serviceWorker.getRegistration();
    return found?.waiting != null;
  } catch {
    return false;
  }
}

/**
 * Mitteilungen auf diesem Gerät einschalten. Nur nach einem Tipp aufrufen (fragt die Erlaubnis des Geräts). Wirft einen
 * Fehler mit lesbarem Text, wenn die Person ablehnt oder etwas schiefgeht.
 */
export async function enableOnThisDevice(vapidPublicKey: string): Promise<PushDevice> {
  const support = pushSupport();
  if (!support.ok) throw new PushError(blockText(support.reason));
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new PushError(
      permission === 'denied'
        ? blockText('denied')
        : 'Ohne Erlaubnis Ihres Geräts gibt es keine Mitteilungen. Sie können es jederzeit erneut versuchen.',
    );
  }
  const worker = await registration();
  const options = { userVisibleOnly: true, applicationServerKey: keyToBytes(vapidPublicKey) };
  let subscription: PushSubscription;
  try {
    subscription = await worker.pushManager.subscribe(options);
  } catch (error) {
    // Ein Abo mit anderem Schlüssel (der Server hat den Schlüssel gewechselt) blockiert das neue: erst entfernen
    if (error instanceof DOMException && error.name === 'InvalidStateError') {
      const old = await worker.pushManager.getSubscription();
      await old?.unsubscribe();
      subscription = await worker.pushManager.subscribe(options);
    } else {
      throw error;
    }
  }
  const json = subscription.toJSON();
  const p256dh = json.keys?.['p256dh'];
  const auth = json.keys?.['auth'];
  if (json.endpoint === undefined || p256dh === undefined || auth === undefined) {
    await subscription.unsubscribe();
    throw new PushError('Dieses Gerät liefert keine vollständigen Angaben für Mitteilungen.');
  }
  try {
    const device = await pushApi.subscribe({ endpoint: json.endpoint, keys: { p256dh, auth } });
    remember(device.id);
    return device;
  } catch (error) {
    // Der Server hat das Gerät nicht angenommen: das lokale Abo nicht liegen lassen
    await subscription.unsubscribe().catch(() => false);
    throw error;
  }
}

/** Das lokale Abo des Browsers beenden (kein Server-Aufruf). Wirft nie. */
export async function endLocalSubscription(): Promise<void> {
  try {
    if (!('serviceWorker' in navigator)) return;
    const found = await navigator.serviceWorker.getRegistration();
    const subscription = await found?.pushManager.getSubscription();
    await subscription?.unsubscribe();
  } catch {
    // nichts zu tun
  }
}

/** Mitteilungen auf diesem Gerät ausschalten: Abo beim Server löschen (die Kennung ist lokal gemerkt) und im Browser beenden. */
export async function disableOnThisDevice(): Promise<void> {
  const id = rememberedSubscriptionId();
  if (id !== null) {
    try {
      await pushApi.remove(id);
    } catch (error) {
      // Das Abo gibt es beim Server schon nicht mehr: Ziel erreicht
      if (!(error instanceof ApiClientError && error.status === 404)) throw error;
    }
  }
  remember(null);
  await endLocalSubscription();
}

/** Beim Abmelden (Server-Logout gelungen): lokales Abo beenden und Kennung vergessen. Wirft nie. */
export async function forgetSubscription(): Promise<void> {
  remember(null);
  await endLocalSubscription();
}

/** Text zu einem Hindernis (Oberfläche und Fehlermeldung). */
export function blockText(reason: PushBlock): string {
  switch (reason) {
    case 'ios-install':
      return 'Auf dem iPhone und iPad gibt es Mitteilungen erst, wenn BARSO auf dem Home-Bildschirm liegt: In Safari auf „Teilen“ tippen, „Zum Home-Bildschirm“ wählen und BARSO von dort öffnen. Es braucht iOS 16.4 oder neuer.';
    case 'native':
      return 'In der App kommen Mitteilungen in einer späteren Version. Bis dahin bekommen Sie die E-Mail-Hinweise. Im Browser oder in der auf dem Home-Bildschirm installierten Web-App geht es schon jetzt.';
    case 'denied':
      return 'Ihr Gerät blockiert Mitteilungen für BARSO. Erlauben Sie sie in den Einstellungen Ihres Browsers oder Geräts und versuchen Sie es dann erneut.';
    case 'browser':
      return 'Dieser Browser kann keine Mitteilungen anzeigen. Probieren Sie einen aktuellen Browser oder installieren Sie BARSO als Web-App.';
  }
}
